<!-- Design it for me. THIS FILE IS YOURS.

     Three kitchens, drawn from above, each with a name, what it might cost and the two or
     three things a designer would say for it. Tap one and it is yours; the one you had is
     one Undo away. "Compare" puts them side by side on the things that differ -- drawers,
     cabinets, price -- because three cards read one at a time are three decisions and a
     table is one. "Show me others" walks further down each taste's list. Nothing here knows
     how the options were made -- a generator today, a model later -- only what they are. -->
<template>
  <div class="options">
    <div class="head">
      <p class="label">Three kitchens for this room</p>
      <button v-if="options.length > 1" type="button" class="toggle" :class="{ on: comparing }" @click="comparing = !comparing">
        {{ comparing ? 'Cards' : 'Compare' }}
      </button>
    </div>

    <!-- WHAT HAD TO GIVE, when something did.
         The generator used to answer an impossible lock set with nothing at all, and the
         sheet said "Nothing fits with the cabinets you have locked where they are. Unlock
         one, or change the room, and try again." — which left somebody looking at an empty
         room having just asked for a kitchen. It now lets go of one lock at a time, and
         fills the runs if even that fails, so the honest line is what it DID. -->
    <p class="gave" v-if="note">{{ note }}</p>

    <p class="none" v-if="!options.length">
      There is nowhere in this room to put a run of cabinets yet.
      <span v-if="reason">{{ reason }}</span>
      Tap a wall to put cabinets on it, then try again.
    </p>

    <!-- side by side: one row per thing that differs -->
    <div v-else-if="comparing" class="compare">
      <table>
        <thead>
          <tr>
            <th></th>
            <th v-for="o in rows" :key="o.id">
              <span class="thumb small" v-html="thumb(o, 96, 68)" />
              <span class="name">{{ o.name }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in measures" :key="m.k">
            <th>{{ m.label }}</th>
            <td v-for="o in rows" :key="o.id" :class="{ best: m.best && o[m.k] === m.best }">{{ m.fmt(o[m.k]) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th></th>
            <td v-for="o in rows" :key="o.id">
              <button type="button" class="pick" @click="$emit('choose', o.option)">Choose</button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <ul v-else class="cards">
      <li v-for="o in rows" :key="o.id">
        <button type="button" class="card" :class="{ on: o.id === chosen }"
                @click="$emit('choose', o.option)">
          <span class="thumb" v-html="thumb(o, 132, 96)" />
          <span class="text">
            <span class="name">{{ o.name }}<span v-if="o.priceText" class="price">{{ o.priceText }}</span></span>
            <span class="blurb">{{ o.blurb }}</span>
            <span class="why">
              <span v-for="(w, i) in o.why" :key="i">{{ w }}</span>
            </span>
          </span>
        </button>
      </li>
    </ul>

    <!-- ── THE WAY ON ──────────────────────────────────────────────────────────────
         Only once one has been taken. A kitchen chosen is not the end of anything -- the
         finishes are a whole tab of decisions after it -- and until this button existed
         the only thing that said so was the tab bar, which says the same thing whether you
         have chosen a kitchen or not. -->
    <div v-if="took" class="chosen">
      <p class="took">You chose <b>{{ took.name }}</b>. It is on screen behind this sheet.</p>
      <button type="button" class="next" @click="$emit('next')">
        Next: choose finishes
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
      </button>
    </div>

    <div class="row">
      <button type="button" class="quiet" @click="$emit('more')">
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10a6 6 0 0 1 10.2-4.3L16 7.5"/><path d="M16 4v3.5h-3.5"/><path d="M16 10a6 6 0 0 1-10.2 4.3L4 12.5"/><path d="M4 16v-3.5h3.5"/></svg>
        Show me others
      </button>
      <span class="fine" v-if="locks.length">
        Keeping {{ locks.map((l) => l.role).join(', ') }} where {{ locks.length > 1 ? 'they are' : 'it is' }}.
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { planSvg } from '../kitchen/plan-svg.js';
import { estimate, money } from '../kitchen/price.js';

const props = defineProps({
  options: { type: Array, default: () => [] },
  rejected: { type: Array, default: () => [] },
  /** One sentence saying what the generator had to let go of, from `designOrFill`. */
  note: { type: String, default: '' },
  locks: { type: Array, default: () => [] },
  /** The finishes the options will wear: the plan on screen, whose runs the options replace. */
  plan: Object,
  maker: Object,
  /** The id of the option already taken, when the person has come back to look again. */
  chosen: { type: String, default: null },
});
defineEmits(['choose', 'more', 'next']);

const comparing = ref(false);
const thumb = (o, w, h) => planSvg(o.option.plan, props.maker, { width: w, height: h, pad: 8 });
// Computed, not read once at setup: the sheet is kept alive between designs, so a reason
// captured on the first "Design it for me" was still on screen after the third.
const reason = computed(() => (props.rejected?.[0]?.reason ? `(${props.rejected[0].reason}.)` : ''));

/** Each option with the numbers a table compares. */
const rows = computed(() => props.options.map((o) => {
  const specs = props.maker.cabinets;
  const items = o.plan.runs.flatMap((r) => r.items).filter((i) => i.specId && !i.gap);
  const of = (i) => specs.find((c) => c.id === i.specId);
  const drawers = items.reduce((n, i) => n + (of(i)?.drawers ?? 0), 0);
  const doors = items.reduce((n, i) => n + (of(i)?.doors ?? 0), 0);
  const tall = items.filter((i) => of(i)?.kind === 'tall').length;
  const bench = o.plan.runs.filter((r) => r.kind === 'base').reduce((n, r) => n + r.items.reduce((m, i) => m + i.w, 0), 0) / 1000;
  const est = props.plan ? estimate({ ...props.plan, runs: o.plan.runs }, props.maker) : null;
  return {
    id: o.id, name: o.name, blurb: o.blurb, why: o.why, option: o,
    cabinets: items.length, drawers, doors, tall, bench: +bench.toFixed(1),
    price: est?.total ?? null, priceText: est?.text ?? '',
  };
}));

/** The one that was taken, if it is still among the three on screen. */
const took = computed(() => rows.value.find((r) => r.id === props.chosen) ?? null);

const measures = computed(() => {
  const r = rows.value;
  const min = (k) => Math.min(...r.map((x) => x[k] ?? Infinity));
  const max = (k) => Math.max(...r.map((x) => x[k] ?? -Infinity));
  const out = [
    { k: 'cabinets', label: 'Cabinets', fmt: (v) => v },
    { k: 'drawers', label: 'Drawers', fmt: (v) => v, best: max('drawers') },
    { k: 'doors', label: 'Doors', fmt: (v) => v },
    { k: 'tall', label: 'Tall units', fmt: (v) => v },
    { k: 'bench', label: 'Bench', fmt: (v) => `${v}m`, best: max('bench') },
  ];
  // To the nearest hundred: it is a band, and a table of exact dollars pretends otherwise.
  if (r.some((x) => x.price)) out.push({ k: 'price', label: 'Estimate', fmt: (v) => (v ? '≈' + money(Math.round(v / 100) * 100) : '—'), best: min('price') });
  return out;
});
</script>

<style scoped>
.options { padding: 0 16px 2px; }
.head { display: flex; align-items: center; gap: 10px; margin: 0 0 10px; }
.label {
  flex: 1; margin: 0;
  font: 500 11.5px/1 var(--ion-font-family, system-ui);
  letter-spacing: .05em; text-transform: uppercase; color: var(--k-mute);
}
.toggle {
  border: 1px solid var(--k-line); border-radius: 8px; background: var(--k-paper); color: var(--k-dim);
  padding: 7px 11px; min-height: 32px; cursor: pointer; font: 500 12px/1 var(--ion-font-family, system-ui);
}
.toggle.on { background: var(--k-ink); color: var(--k-paper); border-color: var(--k-ink); }
.gave {
  margin: 0 0 10px; padding: 9px 11px; border-radius: 10px;
  background: var(--k-fill); color: var(--k-dim);
  font: 400 12.5px/1.45 var(--ion-font-family, system-ui);
}
.none { margin: 0 0 10px; font: 400 13.5px/1.5 var(--ion-font-family, system-ui); color: var(--k-dim); }
.cards { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.card {
  display: flex; gap: 12px; align-items: stretch; width: 100%; text-align: left;
  padding: 10px; border-radius: 13px; cursor: pointer;
  border: 1px solid var(--k-line); background: var(--k-paper); color: var(--k-ink);
}
.card:active { background: var(--k-fill); }
.card.on { border-color: var(--k-ink); box-shadow: inset 0 0 0 1px var(--k-ink); }

.chosen { margin-top: 10px; }
.took { margin: 0 0 8px; font: 400 12.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim); }
.took b { font-weight: 600; color: var(--k-ink); }
.next {
  width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 0; border-radius: 11px; cursor: pointer; padding: 14px; min-height: 50px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 14.5px/1 var(--ion-font-family, system-ui);
}
.next:active { opacity: .9; }
.thumb { flex: 0 0 132px; height: 96px; border-radius: 9px; overflow: hidden; background: var(--k-room); }
.thumb :deep(svg) { display: block; width: 100%; height: 100%; }
.text { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.name { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; font: 600 14.5px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink); }
.price { font: 500 12px/1.2 var(--ion-font-family, system-ui); color: var(--k-accent); }
.blurb { font: 400 12px/1.3 var(--ion-font-family, system-ui); color: var(--k-mute); }
.why { margin-top: 4px; display: grid; gap: 2px; }
.why span {
  font: 400 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-dim);
  padding-left: 10px; position: relative;
}
.why span::before { content: ''; position: absolute; left: 0; top: 6px; width: 4px; height: 4px; border-radius: 999px; background: var(--k-accent); }

.compare { overflow-x: auto; margin: 0 -16px; padding: 0 16px; }
table { border-collapse: collapse; width: 100%; min-width: 320px; }
thead th { vertical-align: top; text-align: center; padding: 0 4px 8px; }
thead th .thumb.small { display: block; width: 96px; height: 68px; margin: 0 auto 6px; }
thead th .name { display: block; text-align: center; font-size: 12.5px; }
tbody th { text-align: left; font: 400 12px/1.3 var(--ion-font-family, system-ui); color: var(--k-mute); padding: 7px 4px; border-top: 1px solid var(--k-line); }
tbody td { text-align: center; font: 500 13px/1.3 var(--ion-font-family, system-ui); color: var(--k-ink); padding: 7px 4px; border-top: 1px solid var(--k-line); font-variant-numeric: tabular-nums; }
tbody td.best { color: var(--k-accent); }
tfoot td { padding: 10px 4px 0; text-align: center; }
.pick {
  border: 0; border-radius: 9px; cursor: pointer; padding: 9px 14px; min-height: 36px;
  background: var(--k-ink); color: var(--k-paper); font: 600 12.5px/1 var(--ion-font-family, system-ui);
}
.row { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
.quiet {
  display: inline-flex; align-items: center; gap: 8px;
  border: 0; background: none; cursor: pointer; padding: 8px 2px; min-height: 40px;
  font: 500 12.5px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}
.fine { margin: 0; font: 400 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-mute); }
</style>
